import { useCallback, useRef, useState } from 'react'

const BOTTOM_THRESHOLD_PX = 48

const distanceFromBottom = (el: HTMLElement) => el.scrollHeight - el.scrollTop - el.clientHeight

export const useStickToBottom = () => {
  const elRef = useRef<HTMLElement | null>(null)
  const lastScrollTopRef = useRef(0)
  const isAtBottomRef = useRef(true)
  const [isAtBottom, setIsAtBottom] = useState(true)

  const setAtBottom = useCallback((value: boolean) => {
    isAtBottomRef.current = value
    setIsAtBottom(value)
  }, [])

  const handleScroll = useCallback(() => {
    const el = elRef.current
    if (!el) return
    const distance = distanceFromBottom(el)
    const movedUp = el.scrollTop < lastScrollTopRef.current
    lastScrollTopRef.current = el.scrollTop

    // Any upward move detaches (ArrowUp is 40px); distance ~0 means content shrank, not the reader scrolling.
    if (movedUp && distance > 1) setAtBottom(false)
    else if (distance <= BOTTOM_THRESHOLD_PX) setAtBottom(true)
  }, [setAtBottom])

  // A callback ref, because the container unmounts whenever the chat widget collapses.
  const scrollRef = useCallback(
    (node: HTMLElement | null) => {
      elRef.current?.removeEventListener('scroll', handleScroll)
      elRef.current = node
      lastScrollTopRef.current = node?.scrollTop ?? 0
      node?.addEventListener('scroll', handleScroll, { passive: true })
      setAtBottom(true)
    },
    [handleScroll, setAtBottom],
  )

  const scrollToBottom = useCallback(
    (behavior: ScrollBehavior = 'smooth') => {
      elRef.current?.scrollTo({ top: elRef.current.scrollHeight, behavior })
      setAtBottom(true)
    },
    [setAtBottom],
  )

  // Instant, because a smooth scroll restarted on every streamed chunk jitters.
  const followIfAtBottom = useCallback(() => {
    if (isAtBottomRef.current) scrollToBottom('instant')
  }, [scrollToBottom])

  return { scrollRef, isAtBottom, scrollToBottom, followIfAtBottom }
}
